import { Pause, PlayArrow } from '@mui/icons-material';
import { Box, IconButton, useTheme } from '@mui/material';
import { alpha } from '@mui/material/styles';
import React, { useEffect, useRef } from 'react';
import * as styles from './TrimEditor.styles';
import { TimeInput } from './TimeInput';
import { MAX_CLIP_DURATION_MS, MIN_CLIP_DURATION_MS } from './types';
import { useTrimWavesurfer } from './useTrimWavesurfer';

export interface TrimEditorProps {
  trackDurationMs: number;
  startMs: number;
  endMs: number;
  onChange: (range: { startMs: number; endMs: number }) => void;
  /** Full-track audio URL. When absent, preview plays a generated placeholder track. */
  audioUrl?: string;
  /** Called when audible preview playback starts. */
  onPlayStart?: () => void;
}

export const TrimEditor: React.FC<TrimEditorProps> = ({
  trackDurationMs,
  startMs,
  endMs,
  onChange,
  audioUrl,
  onPlayStart,
}) => {
  const theme = useTheme();
  const containerRef = useRef<HTMLDivElement | null>(null);

  const playback = useTrimWavesurfer({
    containerRef,
    trackDurationMs,
    startMs,
    endMs,
    minDurationMs: MIN_CLIP_DURATION_MS,
    maxDurationMs: MAX_CLIP_DURATION_MS,
    audioUrl,
    waveColor: alpha(theme.palette.common.white, 0.32),
    progressColor: theme.palette.primary.main,
    cursorColor: theme.palette.common.white,
    // A light wash over the selection approximates the old design's brighter
    // in-range bars (wavesurfer has no per-region bar recoloring), layered
    // under the border/handles drawn via `styles.waveform`'s `::part` rules.
    regionColor: alpha(theme.palette.common.white, 0.15),
    onChange,
    onPlayStart,
  });

  // Spacebar toggles playback while the editor is mounted, except when focus
  // is in a text field (so typing a space in Clip name / time inputs still
  // works as expected).
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.code !== 'Space' && e.key !== ' ') return;
      const target = e.target as HTMLElement | null;
      const tag = target?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || target?.isContentEditable) {
        return;
      }
      e.preventDefault();
      playback.toggle();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [playback]);

  return (
    <Box sx={styles.container}>
      <Box ref={containerRef} sx={styles.waveform(theme)} />

      {/* Controls row — start time, play, end time, sitting below the waveform. */}
      <Box sx={styles.controlsRow}>
        <TimeInput
          valueMs={startMs}
          minMs={Math.max(0, endMs - MAX_CLIP_DURATION_MS)}
          maxMs={endMs - MIN_CLIP_DURATION_MS}
          onChange={(newStart) => onChange({ startMs: newStart, endMs })}
          ariaLabel="Clip start time"
        />

        <IconButton
          onClick={playback.toggle}
          aria-label={playback.isPlaying ? 'Pause preview' : 'Play preview'}
          sx={styles.playButton(theme.palette.mode === 'dark')}
        >
          {playback.isPlaying ? (
            <Pause sx={styles.playIcon} />
          ) : (
            <PlayArrow sx={styles.playIcon} />
          )}
        </IconButton>

        <TimeInput
          valueMs={endMs}
          minMs={startMs + MIN_CLIP_DURATION_MS}
          maxMs={Math.min(trackDurationMs, startMs + MAX_CLIP_DURATION_MS)}
          onChange={(newEnd) => onChange({ startMs, endMs: newEnd })}
          ariaLabel="Clip end time"
        />
      </Box>
    </Box>
  );
};
